import { Box, Button, Stack, Typography } from "@mui/joy";
import { TaskEditorActionComponent } from "pages/task-editor/utils/taskEditorUtils";
import { PreviewSessionApi } from "./usePreviewSession";
import { previewInputSheetSx } from "./previewStyles";
import { theme } from "styles/theme";
import { useState } from "react";

// v1 의 Selection variant 는 color="primary" (선택) / "secondary" (미선택) 으로 토글.
// theme.ts lines 778–843 참조. width/flex 만 우리가 지정, 시각은 variant 가 처리.
const previewSelectionButtonSx = {
  width: { xs: "100%", sm: "auto" },
  flex: { sm: "1 1 auto" },
};

// 옵션 컨테이너 — v1 ButtonGroupMultipleChoiceCell 의 grid xs / flex sm 패턴.
const previewOptionsContainerSx = {
  width: "100%",
  display: { xs: "grid", sm: "flex" },
  gridTemplateColumns: { xs: "repeat(2, 1fr)" },
  flexWrap: "wrap",
  gap: 1,
  p: "4px",
};

interface PreviewActionProps {
  action: TaskEditorActionComponent;
  blockIndex: number; // 0-based
  blockSequence: number; // 1-based
  mode: "static" | "interactive";
  session: PreviewSessionApi;
  viewerMode: "patient" | "coach";
  totalBlocks: number;
}

function PreviewAction({
  action,
  blockIndex,
  blockSequence,
  mode,
  session,
  viewerMode,
}: PreviewActionProps) {
  const isInteractive = mode === "interactive";

  switch (action.type) {
    case "text_input":
      return (
        <Stack spacing="12px">
          {action.fields.map((field, fieldIdx) => (
            <TextInputField
              key={fieldIdx}
              field={field}
              blockIndex={blockIndex}
              fieldIndex={fieldIdx}
              isInteractive={isInteractive}
              session={session}
            />
          ))}
        </Stack>
      );

    case "button_default":
      return (
        <Box sx={previewOptionsContainerSx}>
          {action.options.map((opt, optIdx) => {
            const selections = session.state.buttonSelections[blockIndex] ?? [];
            const isSelected = selections.includes(optIdx);
            return (
              <Button
                key={optIdx}
                variant={"Selection" as any}
                size="md"
                color={isSelected ? "primary" : "neutral"}
                sx={{
                  ...previewSelectionButtonSx,
                  ...(isInteractive ? {} : { pointerEvents: "none" }),
                }}
                onClick={
                  isInteractive
                    ? () => session.toggleButtonDefault(blockIndex, optIdx)
                    : undefined
                }
              >
                {opt.text || "(옵션 없음)"}
              </Button>
            );
          })}
        </Box>
      );

    case "button_quiz":
      return (
        <QuizButtons
          action={action}
          blockIndex={blockIndex}
          isInteractive={isInteractive}
          viewerMode={viewerMode}
          session={session}
        />
      );

    case "button_branch":
      return (
        <Stack spacing="8px">
          <Box sx={previewOptionsContainerSx}>
            {action.options.map((opt, optIdx) => {
              const selections =
                session.state.buttonSelections[blockIndex] ?? [];
              const isSelected = selections.includes(optIdx);
              return (
                <Button
                  key={optIdx}
                  variant={"Selection" as any}
                  size="md"
                  color={isSelected ? "primary" : "neutral"}
                  sx={{
                    ...previewSelectionButtonSx,
                    ...(isInteractive ? {} : { pointerEvents: "none" }),
                  }}
                  onClick={
                    isInteractive
                      ? () => session.chooseBranch(blockIndex, optIdx)
                      : undefined
                  }
                >
                  {opt.text || "(옵션 없음)"}
                </Button>
              );
            })}
          </Box>
          {viewerMode === "coach" && (
            <Stack spacing="2px" sx={{ pl: "4px" }}>
              {action.options.map((opt, optIdx) => {
                const showFrom = opt.show_from ?? blockSequence + 1;
                return (
                  <Typography
                    key={optIdx}
                    variant="Caption"
                    sx={{ color: theme.vars.palette.Content.Weak }}
                  >
                    {`옵션 ${optIdx + 1} → 블록 ${showFrom}`}
                  </Typography>
                );
              })}
            </Stack>
          )}
        </Stack>
      );

    default: {
      const _never: never = action;
      void _never;
      return null;
    }
  }
}

// 텍스트 입력 필드 개별 컴포넌트
function TextInputField({
  field,
  blockIndex,
  fieldIndex,
  isInteractive,
  session,
}: {
  field: { label?: string; placeholder?: string; help_text?: string };
  blockIndex: number;
  fieldIndex: number;
  isInteractive: boolean;
  session: PreviewSessionApi;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const value = session.state.textInputs[blockIndex]?.[fieldIndex] ?? "";

  return (
    <Stack spacing="6px">
      {field.label && (
        <Typography
          variant="Caption"
          sx={{
            color: theme.vars.palette.Content.Highlight,
            fontWeight: 700,
          }}
        >
          {field.label}
        </Typography>
      )}
      <Box
        sx={{
          ...previewInputSheetSx,
          border: isFocused
            ? `1.4px solid ${theme.vars.palette.Border.Highlight}`
            : `1.4px solid ${theme.vars.palette.Border.Weak}`,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {isInteractive ? (
          <Box
            component="textarea"
            value={value}
            placeholder={field.placeholder ?? ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              session.setTextInput(blockIndex, fieldIndex, e.target.value)
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              width: "100%",
              border: "none",
              outline: "none",
              resize: "none",
              backgroundColor: "transparent",
              fontFamily: "inherit",
              fontSize: "14px",
              lineHeight: 1.5,
              color: theme.vars.palette.Content.Strong,
              minHeight: "40px",
              "::placeholder": {
                color: theme.vars.palette.Content.Weak,
              },
            }}
          />
        ) : (
          <Box
            sx={{
              fontSize: "14px",
              lineHeight: 1.5,
              color: theme.vars.palette.Content.Weak,
              minHeight: "40px",
              pointerEvents: "none",
              width: "100%",
            }}
          >
            {field.placeholder && (
              <span style={{ opacity: 0.6 }}>{field.placeholder}</span>
            )}
          </Box>
        )}
      </Box>
      {field.help_text && (
        <Typography
          variant="Caption"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          {field.help_text}
        </Typography>
      )}
    </Stack>
  );
}

// 퀴즈 버튼 — v1 Selection variant. 오답 시각 변화 없음 (v1 호환).
// 정답이면 해당 옵션만 primary 색 표시 + advance.
function QuizButtons({
  action,
  blockIndex,
  isInteractive,
  viewerMode,
  session,
}: {
  action: Extract<TaskEditorActionComponent, { type: "button_quiz" }>;
  blockIndex: number;
  isInteractive: boolean;
  viewerMode: "patient" | "coach";
  session: PreviewSessionApi;
}) {
  const quizState = session.state.quizState[blockIndex];
  const isCorrect = quizState?.correct === true;
  const triedCount = quizState?.tried.length ?? 0;
  // 한 번이라도 시도해서 정답을 못 맞춘 상태면 오답 안내 노출
  const showIncorrectAlert =
    isInteractive && !isCorrect && triedCount > 0 && !!action.incorrect_message;

  return (
    <Stack spacing="8px">
      <Box sx={previewOptionsContainerSx}>
        {action.options.map((opt, optIdx) => {
          const isThisCorrect = opt.is_correct === true;
          const isSelected = isCorrect && isThisCorrect;

          return (
            <Button
              key={optIdx}
              variant={"Selection" as any}
              size="md"
              color={isSelected ? "primary" : "neutral"}
              sx={{
                ...previewSelectionButtonSx,
                ...(isInteractive && !isCorrect
                  ? {}
                  : { pointerEvents: "none" }),
              }}
              onClick={
                isInteractive && !isCorrect
                  ? () => session.attemptQuiz(blockIndex, optIdx)
                  : undefined
              }
            >
              {opt.text || "(옵션 없음)"}
            </Button>
          );
        })}
      </Box>
      {showIncorrectAlert && (
        <Typography
          variant="Caption"
          sx={{
            color: theme.vars.palette.Content.Negative,
            fontWeight: 600,
            pl: "4px",
          }}
        >
          {action.incorrect_message}
        </Typography>
      )}
      {/* coach 정적 모드 진단 정보: 정답 표시 + 오답 메시지 */}
      {viewerMode === "coach" && !isInteractive && (
        <Stack spacing="2px" sx={{ pl: "4px" }}>
          {action.options.map((opt, optIdx) => {
            if (opt.is_correct !== true) return null;
            return (
              <Typography
                key={optIdx}
                variant="Caption"
                sx={{
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,
                }}
              >
                {`옵션 ${optIdx + 1}: (정답)`}
              </Typography>
            );
          })}
          {action.incorrect_message && (
            <Typography
              variant="Caption"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              {`오답 시: ${action.incorrect_message}`}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default PreviewAction;
